import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const AssetDetail = lazy(() => import('@/pages/AssetDetail'));
const Strategy = lazy(() => import('@/pages/Strategy'));
const Performance = lazy(() => import('@/pages/Performance'));
const TradeDetails = lazy(() => import('@/pages/TradeDetails'));
const News = lazy(() => import('@/pages/News'));
const GoldHub = lazy(() => import('@/pages/GoldHub'));
import Login from '@/pages/Login';
import Register from '@/pages/Register';
const Terms = lazy(() => import('@/pages/Terms'));
const AdminPanel = lazy(() => import('@/pages/AdminPanel'));
const ScalpBotPage = lazy(() => import('@/pages/ScalpBotPage'));
const AIRecommendations = lazy(() => import('@/pages/AIRecommendations'));
const AISignalsGuide = lazy(() => import('@/pages/AISignalsGuide'));
const AssetDetailGuide = lazy(() => import('@/pages/AssetDetailGuide'));
const MyTrades = lazy(() => import('@/pages/MyTrades'));
const LearningPage = lazy(() => import('@/pages/LearningPage'));
import Subscribe from '@/pages/Subscribe';
import ResetPassword from '@/pages/ResetPassword';
const Maintenance = lazy(() => import('@/pages/Maintenance'));
import Navigation from '@/components/Navigation';
import NotificationListener from '@/components/NotificationListener';
import EconomicAlert from '@/components/EconomicAlert';
import UserChat from '@/components/UserChat';
import { Toaster } from '@/components/ui/sonner';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';

// 🔐 مكون لحماية الصفحات
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Subscription check (admin exempt)
  if (user?.role !== 'admin' && user?.subscription_status !== 'active') {
    return <Navigate to="/subscribe" replace />;
  }
  
  return children;
};

// مكون للصفحات التي تحتاج تسجيل دخول فقط (بدون اشتراك)
const AuthOnlyRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// 🔐 مكون لحماية صفحة المدير
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// 🔐 مكون لإعادة التوجيه إذا كان المستخدم مسجل دخوله
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// 🧪 Hides global chrome (Navigation, EconomicAlert ticker, support chat) on
// dedicated full-screen pages like /scalp-bot so they don't clutter the UI.
const FULLSCREEN_ROUTES = ['/scalp-bot'];
// Routes where the top Navigation should ALWAYS render for any authenticated user
// (bypasses the active-subscription gate). Currently: the Learning Center.
const OPEN_NAV_ROUTES = ['/learn'];
const GlobalChrome = ({ isAuthenticated, user }) => {
  const location = useLocation();
  const isFullscreen = FULLSCREEN_ROUTES.includes(location.pathname);
  if (isFullscreen) return null;
  const isOpenNav = OPEN_NAV_ROUTES.includes(location.pathname);
  const canShowNav = isAuthenticated && (user?.role === 'admin' || user?.subscription_status === 'active' || isOpenNav);
  return (
    <>
      {canShowNav && <Navigation />}
      {isAuthenticated && <EconomicAlert />}
      {isAuthenticated && <UserChat />}
    </>
  );
};

function AppContent() {
  const { isAuthenticated, user, loading } = useAuth();
  const [maintenance, setMaintenance] = useState({ enabled: false, message: '' });
  const [maintenanceLoaded, setMaintenanceLoaded] = useState(false);

  // Check maintenance status from DB (instant, no deploy needed)
  useEffect(() => {
    fetch('/api/settings/maintenance')
      .then(r => r.json())
      .then(data => { setMaintenance(data); setMaintenanceLoaded(true); })
      .catch(() => setMaintenanceLoaded(true));
    
    // Re-check every 30 seconds
    const interval = setInterval(() => {
      fetch('/api/settings/maintenance')
        .then(r => r.json())
        .then(data => setMaintenance(data))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Maintenance Mode: block authenticated non-admin users only
  // Non-authenticated users can still reach login page
  if (maintenanceLoaded && maintenance.enabled && !loading) {
    if (isAuthenticated && user?.role !== 'admin') {
      return <Maintenance message={maintenance.message} />;
    }
  }

  
  return (
    <div className="App min-h-screen bg-[#050505] text-white">
      <BrowserRouter>
        <GlobalChrome
          isAuthenticated={isAuthenticated}
          user={user}
        />
        
        {/* مكون الاستماع للإشعارات */}
        <NotificationListener />
        
        <Suspense fallback={<div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div></div>}>
        <Routes>
          {/* صفحات عامة */}
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* صفحة الاشتراك - تحتاج تسجيل دخول فقط */}
          <Route path="/subscribe" element={
            <AuthOnlyRoute>
              <Subscribe />
            </AuthOnlyRoute>
          } />
          
          {/* صفحات محمية */}
          <Route path="/" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/asset/:symbol" element={
            <ProtectedRoute>
              <AssetDetail />
            </ProtectedRoute>
          } />
          <Route path="/strategy" element={
            <ProtectedRoute>
              <Strategy />
            </ProtectedRoute>
          } />
          <Route path="/performance" element={
            <AdminRoute>
              <Performance />
            </AdminRoute>
          } />
          <Route path="/trade-details" element={
            <ProtectedRoute>
              <TradeDetails />
            </ProtectedRoute>
          } />
          <Route path="/news" element={
            <ProtectedRoute>
              <News />
            </ProtectedRoute>
          } />
          <Route path="/gold" element={
            <ProtectedRoute>
              <GoldHub />
            </ProtectedRoute>
          } />
          <Route path="/ai-deals" element={
            <ProtectedRoute>
              <AIRecommendations />
            </ProtectedRoute>
          } />

          {/* صفحة دليل الإشارات */}
          <Route path="/ai-signals-guide" element={
            <ProtectedRoute>
              <AISignalsGuide />
            </ProtectedRoute>
          } />

          {/* صفحة دليل صفحة الإشارة الفردية (Asset Detail) */}
          <Route path="/asset-guide" element={
            <ProtectedRoute>
              <AssetDetailGuide />
            </ProtectedRoute>
          } />

          {/* صفحاتي النشطة (Trade Tracker) */}
          <Route path="/my-trades" element={
            <ProtectedRoute>
              <MyTrades />
            </ProtectedRoute>
          } />
          
          {/* 📚 مركز التعليم — متاح للجميع (بدون تسجيل دخول) */}
          <Route path="/learn" element={<LearningPage />} />

          {/* 🔐 صفحة لوحة التحكم - للمدير فقط */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          } />

          {/* 🤖 بوت السكالب — للمدراء فقط */}
          <Route path="/scalp-bot" element={
            <AdminRoute>
              <ScalpBotPage />
            </AdminRoute>
          } />
          
          {/* صفحة الشروط والأحكام - عامة */}
          <Route path="/terms" element={<Terms />} />
        </Routes>
        </Suspense>

        {/* Global Footer */}
        {isAuthenticated && (user?.role === 'admin' || user?.subscription_status === 'active') && (
          <footer className="py-4 text-center border-t border-white/5 mt-8">
            <p className="text-zinc-600 text-xs">
              © 2025 الرؤية للتداول. جميع الحقوق محفوظة
              <span className="mx-2">|</span>
              <a href="mailto:support@alroyah.com" className="text-zinc-500 hover:text-[#00E5FF] transition-colors">
                support@alroyah.com
              </a>
            </p>
          </footer>
        )}
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
